import { Button, Container, Grid, Paper, Typography } from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { Edit, ExitToApp } from "@material-ui/icons";
import { App } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";

import { calcAge, useStateValue } from "../../config/AppContext";
import { EmailModal, PasswordModal } from "../Modals";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    paper: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      textAlign: "left",
      color: theme.palette.text.secondary,
      "& h6": {
        fontSize: "14px",
        lineHeight: "16px",
        marginTop: theme.spacing(3),
      },
      "& p": {
        color: theme.palette.text.primary,
        fontSize: "15px",
        lineHeight: "18px",
      },
      "& span": {
        display: "block",
        color: theme.palette.text.secondary,
        fontSize: "16px",
        lineHeight: "20px",
      },
    },
    actions: {
      "& button": {
        marginTop: 5,
        height: 56,
        fontSize: 17,
        whiteSpace: "nowrap",
        // padding:"0 30px",
        textTransform: "none",
      },
      "& button:first-child": {
        backgroundColor: "var(--text-primary)",
        "& svg": {
          marginLeft: "calc(100% - 172px)",
        },
      },
      "& button:nth-child(2)": {
        backgroundColor: "#ffffff",
        "& svg": {
          marginLeft: "calc(100% - 80px)",
        },
      },
    },
  })
);

const ProfileComponent: React.FC = () => {
  const classes = useStyles();
  return (
    <div id="Profile">
      <Grid container>
        <Grid item xs={12} md={8}>
          <Paper className={classes.paper}>
            <PersonalInfo />
            <CompanyInfo />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4} className={classes.actions}>
          <ActionSection />
        </Grid>
      </Grid>
    </div>
  );
};

const PersonalInfo: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useStateValue() as { user: App.State["user"] };
  if (!user) return <></>;
  return (
    <>
      <Typography variant="subtitle1" gutterBottom style={{ marginTop: 0 }}>
        {t("Personal information")}
      </Typography>
      <hr />
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Name")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" component="span" noWrap>
            {user.first_name} {user.last_name}
          </Typography>
        </Grid>
      </Grid>
      {/* <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant='body1'>{t("Phone")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant='body2' component='span' noWrap>
            {user.phone}
          </Typography>
        </Grid>
      </Grid> */}
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Gender")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant="body2"
            component="span"
            noWrap
            style={{ textTransform: "capitalize" }}
          >
            {t(user.gender).toLowerCase()}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Birthday")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" component="span" noWrap>
            {user.birthday} ({user && calcAge(user.birthday)} {t("Years").toLowerCase()})
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Country")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" component="span" noWrap>
            {user.country?.name || ""}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Email")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" component="span" noWrap>
            {user.email}
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};
const CompanyInfo: React.FC = () => {
  const { t } = useTranslation();
  const { companies, settings, Methods } = useStateValue() as {
    companies: App.State["companies"];
    settings: App.State["settings"];
    Methods: App.Methods;
  };

  const [toggled, setToggled] = React.useState(settings && settings.include_logo);

  const handleToggle = async () => {
    if (!settings) return;
    await Methods.updateProfile({
      session_length: settings.session_length,
      session_length_long: settings.session_length_long,
      session_interval: settings.session_interval,
      include_logo: !toggled,
    });
    return setToggled(!toggled);
  };

  if (!companies) return <></>;
  return (
    <>
      <Typography variant="subtitle1" gutterBottom>
        {t("Company information")}
      </Typography>
      <hr />
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Name")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" component="span" noWrap>
            {companies.length !== 0 ? companies[0].name : t("Not available")}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Department")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" component="span" noWrap>
            {companies.length !== 0 ? companies[0].department : t("Not available")}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Area code")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" component="span" noWrap>
            {companies.length !== 0 ? companies[0].area_code : t("Not available")}
          </Typography>
        </Grid>
      </Grid>
      <Typography variant="subtitle1" gutterBottom>
        {t("PDF reports")}
      </Typography>
      <hr />
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Include company logo in PDF reports")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <span
            className={toggled ? "toggle-container toggled" : "toggle-container"}
            style={{ float: "none" }}
            onClick={handleToggle}
          >
            <span className={"toggle-button"} />
          </span>
        </Grid>
      </Grid>
    </>
  );
};
const ActionSection: React.FC = () => {
  const { t } = useTranslation();
  const { Methods } = useStateValue() as { Methods: App.Methods };
  const [emailModal, showEmailModal] = React.useState(false);
  const [passwordModal, showPasswordModal] = React.useState(false);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    switch (e.currentTarget.id) {
      case "email":
        return showEmailModal(true);
      case "password":
        return showPasswordModal(true);
      case "logout":
        return Methods.handleLogout();
      default:
        break;
    }
  };
  const handleClose = () => {
    if (emailModal) return showEmailModal(false);
    if (passwordModal) return showPasswordModal(false);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFontElement>, data: { [x: string]: string }) => {
    e.preventDefault();
    switch (e.currentTarget.getAttribute("name")) {
      case "email":
        const { email } = data;
        if (email) {
          return Methods.updateEmail({ email }).then(() => showEmailModal(false));
        }
        return alert(t("Please enter required fields"));
      case "password":
        const { currentPassword, newPassword } = data;
        if (currentPassword && newPassword) {
          return Methods.updatePass({ currentPassword, newPassword }).then(() =>
            showPasswordModal(false)
          );
        }
        return alert(t("Please enter required fields"));
      default:
        break;
    }
  };
  return (
    <Container style={{ marginTop: 20, width: "100%" }}>
      <Button
        fullWidth
        id="password"
        variant="contained"
        color="primary"
        onClick={handleClick}
        style={{ justifyContent: "space-between" }}
      >
        {t("Change password")} <Edit />
      </Button>
      <Button
        fullWidth
        id="logout"
        variant="outlined"
        color="secondary"
        onClick={handleClick}
        style={{ justifyContent: "space-between" }}
      >
        {t("Logout")} <ExitToApp />
      </Button>

      <EmailModal show={emailModal} onHide={handleClose} onSubmit={handleSubmit} />
      <PasswordModal
        fullWidth
        maxWidth="sm"
        open={passwordModal}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </Container>
  );
};

export default ProfileComponent;
